import React, { useState, useMemo, useRef } from 'react'
import { useRouter, Link } from '@tanstack/react-router'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { api } from '../../../shared/ApiProvider'

export default function StudentList({ data, onDelete }) {
  const router = useRouter()
  // Align columns with data property names
  const columns = useMemo(
    () => [
      { label: 'First Name', value: 'firstName' },
      { label: 'Last Name', value: 'lastName' },
      { label: 'PERM Number', value: 'permNumber' },
      { label: 'DOB', value: 'birthDate' },
      { label: 'School', value: 'enrollingAt' },
      { label: 'School District', value: 'enrollingAtDistrict' },
      { label: 'Grade', value: 'gradeEntering' },
    ],
    []
  )

  const [showModal, setShowModal] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const tableRef = useRef(null)

  const selectStudent = permNumber => {
    router.navigate({ to: `/student/${permNumber}` })
  }

  const handleDeleteClick = student => {
    setSelectedStudent(student)
    setShowModal(true)
  }

  const handleConfirmDelete = async () => {
    if (selectedStudent) {
      try {
        await api.delete(`/student/${selectedStudent.permNumber}`)
        setShowModal(false)
        setSelectedStudent(null)
        if (onDelete) onDelete(selectedStudent.permNumber)
        tableRef.current?.focus()
      } catch {
        setShowModal(false)
      }
    }
  }

  const handleCancel = () => {
    setShowModal(false)
    setSelectedStudent(null)
  }

  return (
    <>
      {!data || data.length === 0 ? (
        <div className="text-center mt-3">No students found.</div>
      ) : (
        <Table ref={tableRef} tabIndex={-1} responsive striped bordered hover size="sm" className="mt-3">
          <caption className="visually-hidden">Student search results</caption>
          <thead>
            <tr>
              {columns.map(column => (
                <th scope="col" className="pointer-hover" id={column.value} key={column.value}>
                  {column.label}
                </th>
              ))}
              <th scope="col">Delete</th>
            </tr>
          </thead>
          <tbody>
            {data.map(student => (
              <tr
                key={student.permNumber}
                className="pointer-hover"
                tabIndex={0}
                onClick={() => selectStudent(student.permNumber)}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && selectStudent(student.permNumber)}>
                {columns.map(column => (
                  <td key={column.value}>
                    {column.value === 'firstName' ? (
                      <Link to={`/student/${student.permNumber}`} onClick={e => e.stopPropagation()} aria-label={`View ${student.firstName} ${student.lastName}`}>
                        {student.firstName}
                      </Link>
                    ) : column.value === 'birthDate' ? (
                      new Date(student[column.value]).toLocaleDateString('en-US', { timeZone: 'UTC' })
                    ) : (
                      student[column.value]
                    )}
                  </td>
                ))}
                <td>
                  {onDelete ? (
                    <Button
                      variant="danger"
                      aria-label={`Delete ${student.firstName} ${student.lastName}`}
                      onClick={e => {
                        e.stopPropagation()
                        handleDeleteClick(student)
                      }}>
                      Delete
                    </Button>
                  ) : (
                    <span className="text-muted">N/A</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Modal show={showModal} onHide={handleCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete {selectedStudent ? `${selectedStudent.firstName} ${selectedStudent.lastName}` : 'this student'}?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
